import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

// Material UI Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import EditIcon from '@mui/icons-material/Edit';

import { useProduct } from '../../../context/ProductContext';
import ProductForm from '../../../forms/product/ProductForm';
import { Product } from '../../../models/Product';
import Button from '../../../components/formElements/Button';
import Loading from '../../../components/global/loading/Loading';

function EditProduct() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { getProduct } = useProduct();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true);
      try {
        if (!id) {
          throw new Error('Nieprawidłowy identyfikator produktu');
        }

        const response = await getProduct(Number(id));
        setProduct(response.product);
        setError(null);
      } catch (error: Error | any) {
        console.error('Błąd pobierania produktu:', error);
        setError(error.message || 'Błąd podczas pobierania produktu');
        toast.error('Nie można znaleźć produktu o podanym ID');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id, getProduct]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loading />
      </div>
    );
  }

  if (error || !product) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-red-50 border border-red-200 rounded-lg p-6 text-center"
      >
        <p className="text-red-600 mb-4">
          {error || 'Nie można załadować produktu'}
        </p>
        <Button
          onClick={() => navigate('/admin/produkty')}
          colorTheme="outline"
          className="mx-auto"
        >
          Powrót do listy produktów
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Nagłówek z przyciskiem powrotu */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/admin/produkty')}
              colorTheme="outline"
              className="w-auto px-3 py-1.5"
            >
              <ArrowBackIcon fontSize="small" className="mr-1" />
              Powrót
            </Button>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <EditIcon className="text-primary mr-2" />
              Edytuj produkt
            </h1>
          </div>
          <div className="mt-2 flex items-center">
            <div className="bg-gray-100 rounded-md px-3 py-1 text-gray-700 flex items-center">
              <ShoppingBagIcon
                fontSize="small"
                className="mr-1 text-gray-500"
              />
              <span className="font-medium">{product.name}</span>
              {product.draft && (
                <span className="ml-2 bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded">
                  Wersja robocza
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Formularz produktu */}
      {product && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <ProductForm product={product} inEditMode={true} />
        </div>
      )}
    </motion.div>
  );
}

export default EditProduct;
